import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Users2,
  Flame,
  CalendarHeart,
  Heart,
  Image as ImageIcon,
  Clock,
  MessageCircleHeart,
  BookMarked,
  Music4,
  Sparkles,
  TreePine,
} from "lucide-react";
import { AUTH_SIGNUP_URL, CATEGORIES } from "@/lib/data";
import { MFT, SP } from "@/components/site/BrandLinks";

const ease = [0.22, 1, 0.36, 1];

const MEMORIALS_ILLUSTRATION = "/portraits/wing_memorials.png";

const PLATE = {
  candles: "https://images.unsplash.com/photo-1477414348463-c0eb7f1359b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  album:   CATEGORIES.find((c) => c.slug === "family-stories")?.image,
  letters: CATEGORIES.find((c) => c.slug === "family-history")?.image,
  angel:   "https://images.unsplash.com/photo-1518709594023-6eab9bab7b23?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  memorial: CATEGORIES.find((c) => c.slug === "memorials")?.image,
  celebrate: CATEGORIES.find((c) => c.slug === "celebrate-a-life")?.image,
  portrait: CATEGORIES.find((c) => c.slug === "historical-families")?.image,
};

/* ------------------------------------------------------------------ */
/*  Primitives                                                          */
/* ------------------------------------------------------------------ */

function Chapter({ n, title, subtitle, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease }}
      className="max-w-[1600px] mx-auto px-6 md:px-10 py-14 md:py-20 border-t border-[color:var(--ink-line)]"
    >
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-14 items-end">
        <div className="col-span-12 md:col-span-2">
          <div className="numeric-index text-[color:var(--ink-soft)]">{n}</div>
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(30px, 4vw, 58px)" }}
          >
            {title}
            {subtitle ? (
              <>
                {" "}
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  {subtitle}
                </span>
              </>
            ) : null}
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6 md:gap-10">
        <div className="hidden md:block md:col-span-2" />
        <div className="col-span-12 md:col-span-10">{children}</div>
      </div>
    </motion.section>
  );
}

const Body = ({ children }) => (
  <p className="text-[17px] md:text-[19px] leading-[1.65] max-w-[62ch] text-[color:var(--ink)]">
    {children}
  </p>
);
const Prose = ({ children }) => <div className="space-y-6 md:space-y-7">{children}</div>;

function Ledger({ items, columns = 2 }) {
  const colClass = columns === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return (
    <ul
      className={`mt-2 grid grid-cols-1 ${colClass} gap-x-10 gap-y-0 border-t border-[color:var(--ink-line)]`}
    >
      {items.map((item, i) => (
        <li
          key={i}
          className="flex items-baseline gap-4 py-3 border-b border-[color:var(--ink-line)]"
        >
          <span className="numeric-index text-[color:var(--ink-soft)] w-8 shrink-0">
            {String(i + 1).padStart(2, "0")}
          </span>
          <span
            className="font-serif-display leading-[1.15]"
            style={{ fontSize: "clamp(17px, 1.5vw, 22px)" }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Plate({ src, caption, caption2, aspect = "aspect-[4/5]" }) {
  return (
    <figure className="artifact border border-[color:var(--ink-line-strong)] overflow-hidden">
      <div className={`${aspect} overflow-hidden bg-[color:var(--bone-2)]`}>
        <img src={src} alt="" className="zoom w-full h-full object-cover" loading="lazy" />
      </div>
      <figcaption className="p-4 border-t border-[color:var(--ink-line)] flex items-baseline justify-between gap-4">
        <span className="numeric-index text-[color:var(--ink-soft)]">{caption}</span>
        <span className="numeric-index text-[color:var(--ink-soft)] text-right">{caption2}</span>
      </figcaption>
    </figure>
  );
}

/* ------------------------------------------------------------------ */
/*  Content                                                             */
/* ------------------------------------------------------------------ */

const CELEBRATE_ITEMS = [
  "A life well lived",
  "Family traditions",
  "Personal achievements",
  "Community contributions",
  "Acts of kindness",
  "Lifelong friendships",
  "Inspiring journeys",
  "Favorite memories",
  "Shared experiences",
  "Enduring values",
];

const STORY_ITEMS = [
  "Personal biography",
  "Family history",
  "Childhood memories",
  "Education",
  "Career and accomplishments",
  "Military or public service",
  "Community involvement",
  "Hobbies and passions",
  "Faith and spiritual journey",
  "Travels and adventures",
  "Favorite sayings",
  "Family traditions",
  "Life lessons",
  "Legacy and impact",
];

const MEMORY_ITEMS = [
  "Photographs",
  "Videos",
  "Voice recordings",
  "Letters",
  "Journals",
  "Poems",
  "Recipes",
  "Artwork",
  "Awards and certificates",
  "Newspaper articles",
  "Favorite music",
  "Family stories",
  "Milestone celebrations",
  "Historical documents",
];

const CONTRIBUTIONS = [
  "Personal tributes",
  "Stories and memories",
  "Condolence messages",
  "Photographs",
  "Videos",
  "Audio recordings",
  "Favorite moments",
  "Acts of kindness remembered",
  "Words of gratitude",
  "Anniversary reflections",
];

const LEGACY = [
  "Family photographs",
  "Recorded voices",
  "Life stories",
  "Family traditions",
  "Important achievements",
  "Shared memories",
  "The values that shaped their family",
];

const CONNECT = [
  "Parents",
  "Grandparents",
  "Children",
  "Siblings",
  "Extended family",
  "Family branches",
  "Heritage",
  "Culture",
  "Genealogy",
  "Cemeteries & Mausoleums",
  "Historic homes",
  "Ancestral villages",
  "Community history",
];

const ANNIVERSARIES = [
  "Birth anniversaries",
  "Memorial anniversaries",
  "Wedding anniversaries",
  "Military remembrance days",
  "Community commemorations",
  "Religious observances",
  "Family reunions",
  "Significant life milestones",
];

const HOMES = [
  "The mother who devoted her life to her children.",
  "The father who worked tirelessly to provide for his family.",
  "The grandparent whose stories became family traditions.",
  "The teacher who inspired young minds.",
  "The doctor who healed a community.",
  "The neighbor who was always there to help.",
  "The friend whose kindness changed lives.",
];

const TRADITIONS_INTRO = [
  "Some gather for prayer.",
  "Some light candles or lamps.",
  "Some place flowers.",
  "Some plant trees.",
  "Some prepare favorite meals.",
  "Some visit cemeteries or memorial gardens.",
  "Some share stories, songs, or acts of charity.",
  "Some observe annual days of remembrance.",
];

const LEGACY_LINES = [
  "It is the love we shared.",
  "The kindness we offered.",
  "The lessons we taught.",
  "The people we inspired.",
  "The family we nurtured.",
  "The community we strengthened.",
];

const ETERNAL_LINES = [
  "It deserves a story.",
  "A voice.",
  "A photograph.",
  "A smile remembered.",
  "A legacy preserved.",
];

/* Five illustration pillars */
const PILLARS = [
  { icon: BookOpen,           label: "Honor Their Story",   desc: "Share life stories, photos, videos and cherished memories" },
  { icon: Users2,             label: "Share & Connect",     desc: "Invite family and friends to share tributes and memories" },
  { icon: Flame,              label: "Celebrate Their Life",desc: "Remember the joy, love and beautiful moments" },
  { icon: CalendarHeart,      label: "Remember Always",     desc: "Memorial anniversaries and special dates to honour them" },
  { icon: Heart,              label: "Preserve Their Legacy", desc: "A lasting legacy for future generations to discover" },
];

/* Bottom action bar mirroring illustration icons */
const ACTIONS = [
  { icon: BookOpen,          label: "Life Stories" },
  { icon: ImageIcon,         label: "Photos & Videos" },
  { icon: Clock,             label: "Timeline" },
  { icon: Heart,             label: "Tributes" },
  { icon: MessageCircleHeart,label: "Guest Book" },
  { icon: Music4,            label: "Music" },
  { icon: CalendarHeart,     label: "Memorial Dates" },
  { icon: Sparkles,          label: "Blossoms of Love" },
  { icon: TreePine,          label: "Legacy for Generations" },
];

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function Memorials() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <main data-testid="memorials-page" className="pt-32 md:pt-40">
      {/* ---------------- Masthead ---------------- */}
      <section ref={heroRef} className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="flex items-center gap-3 text-[13px] mb-8">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]">
            Index
          </Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">Chapter V · Memorials</span>
        </div>

        <div className="overline text-[color:var(--rust)] mb-4">
          § Celebrate a Life · Memorials
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          style={{ y: heroY, fontSize: "clamp(46px, 8.6vw, 160px)" }}
          className="font-serif-display leading-[0.9] tracking-[-0.03em] max-w-6xl"
        >
          Honouring lives.{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            Preserving memories. Inspiring generations.
          </span>
        </motion.h1>
      </section>

      {/* ---------------- Pull-quote ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-10 md:pb-16">
        <div className="border-y border-[color:var(--ink)] py-10 md:py-16">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
            <div className="col-span-1">
              <span
                className="font-serif-display text-[color:var(--rust)] leading-none"
                style={{ fontSize: "clamp(60px, 8vw, 120px)" }}
              >
                “
              </span>
            </div>
            <div className="col-span-11">
              <p
                className="font-italic-serif italic leading-[1.15]"
                style={{ fontSize: "clamp(22px, 3.6vw, 52px)" }}
              >
                The measure of a life is not only in the years lived, but in
                the love shared, the lives touched, and the legacy left
                behind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Opening prose ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-20">
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <div className="hidden md:block md:col-span-2">
            <div className="overline text-[color:var(--ink-soft)]">§ Welcome</div>
          </div>
          <div className="col-span-12 md:col-span-10">
            <Prose>
              <p
                className="font-serif-display leading-[1.15]"
                style={{ fontSize: "clamp(22px, 2.4vw, 34px)" }}
              >
                Every life is a unique story.
              </p>
              <Body>
                Each person leaves behind memories, relationships, achievements,
                traditions, and moments that continue to shape the lives of
                family and friends long after they are gone.
              </Body>
              <Body>
                The Memorials section of Celebrate a Life, part of <MFT /> and
                the <SP /> platform, is a place where those stories can be
                preserved with dignity, compassion, and love.
              </Body>
              <p
                className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3]"
                style={{ fontSize: "clamp(20px, 2.2vw, 30px)" }}
              >
                It is more than a memorial page. It is a living tribute where
                families can celebrate a life well lived, remember precious
                moments, and ensure that future generations know the remarkable
                people who came before them.
              </p>
            </Prose>
          </div>
        </div>
      </section>

      {/* ---------------- Featured illustration ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <motion.figure
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.1, ease }}
          className="relative max-w-5xl mx-auto"
        >
          <div className="border border-[color:var(--ink-line-strong)] p-3 md:p-5 bg-[color:var(--bone-2)]/50">
            <div className="overflow-hidden">
              <img
                src={MEMORIALS_ILLUSTRATION}
                alt="A soft, luminous illustration of a memorial garden at sunset with a wooden bench, portrait, blue shawl, lantern, open memory book with sepia photographs, hanging polaroids, candles, a dove and butterflies, and five pillars marked Honor Their Story, Share & Connect, Celebrate Their Life, Remember Always, Preserve Their Legacy — captioned Remember, Honor, Celebrate, Forever."
                className="w-full h-auto block"
                loading="lazy"
                data-testid="memorials-illustration"
              />
            </div>
          </div>
          <figcaption className="mt-4 flex items-start justify-between gap-6">
            <div className="numeric-index text-[color:var(--ink-soft)]">
              Plate № V · Remember · Honour · Celebrate · Forever
            </div>
            <div className="numeric-index text-[color:var(--ink-soft)] text-right max-w-[320px] hidden md:block">
              A life remembered · A love that lives on
            </div>
          </figcaption>
        </motion.figure>
      </section>

      {/* ---------------- Chapter 01 · Every Life Matters ---------------- */}
      <Chapter n="01" title="Every life" subtitle="matters.">
        <Prose>
          <Body>
            History often remembers famous leaders, inventors, artists, and
            pioneers.
          </Body>
          <Body>
            But every family knows that the greatest heroes are often found at
            home.
          </Body>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-3 py-2 my-4">
                {HOMES.map((line, i) => (
                  <p
                    key={i}
                    className="font-italic-serif italic leading-[1.4]"
                    style={{ fontSize: "clamp(18px, 1.75vw, 24px)" }}
                  >
                    {line}
                  </p>
                ))}
              </div>
              <Body>
                Whether a person lived quietly or achieved worldwide
                recognition, every life has meaning.
              </Body>
              <p
                className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3]"
                style={{ fontSize: "clamp(20px, 2.2vw, 30px)" }}
              >
                Every life deserves to be remembered.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.portrait}
                caption="Plate № 001"
                caption2="A quiet hero at home"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 02 · A Celebration, Not Just a Farewell ---------------- */}
      <Chapter n="02" title="A celebration," subtitle="not just a farewell.">
        <Prose>
          <Body>Memorials are not simply about loss.</Body>
          <Body>They are about gratitude.</Body>
          <Body>
            They are about remembering laughter, love, courage, resilience,
            generosity, and hope.
          </Body>
          <Body>Celebrate:</Body>
          <Ledger items={CELEBRATE_ITEMS} columns={2} />
          <p
            className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
          >
            Every memorial becomes a celebration of the person's unique journey.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 03 · Tell Their Story ---------------- */}
      <Chapter n="03" title="Tell" subtitle="their story.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>Behind every name is an extraordinary life.</Body>
              <Body>A memorial can preserve:</Body>
              <Ledger items={STORY_ITEMS} columns={2} />
              <p
                className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3] mt-6"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                The stories we tell today become tomorrow's family history.
              </p>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.album}
                caption="Plate № 002"
                caption2="An album, kept open"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 04 · Preserve Precious Memories ---------------- */}
      <Chapter n="04" title="Preserve precious" subtitle="memories.">
        <Prose>
          <Body>A memorial becomes richer with every memory shared.</Body>
          <Body>Families can preserve:</Body>
          <Ledger items={MEMORY_ITEMS} columns={3} />
          <Body>
            These treasured memories create a lasting digital archive that can
            be visited by generations to come.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 05 · A Place for Family and Friends ---------------- */}
      <Chapter n="05" title="A place for" subtitle="family and friends.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.letters}
                caption="Plate № 003"
                caption2="Letters kept in a drawer"
              />
            </div>
            <div className="lg:col-span-7">
              <Body>Grief is easier to carry when memories are shared.</Body>
              <Body>Family and friends can contribute:</Body>
              <Ledger items={CONTRIBUTIONS} columns={2} />
              <p
                className="font-italic-serif italic text-[color:var(--ink-soft)] leading-[1.3] mt-6"
                style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
              >
                Together, these contributions create a collective remembrance
                that reflects the many lives one person touched.
              </p>
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 06 · Honouring Every Culture and Every Faith ---------------- */}
      <Chapter n="06" title="Honouring every culture" subtitle="and every faith.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>
                Across the world, people remember their loved ones in many
                beautiful ways.
              </Body>
              <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-2 py-2 my-4">
                {TRADITIONS_INTRO.map((line, i) => (
                  <p
                    key={i}
                    className="font-italic-serif italic leading-[1.4]"
                    style={{ fontSize: "clamp(17px, 1.6vw, 22px)" }}
                  >
                    {line}
                  </p>
                ))}
              </div>
              <Body>
                Celebrate a Life respectfully welcomes families from every
                culture, religion, and tradition. Every memorial is created
                with dignity, compassion, and respect for the customs that are
                meaningful to each family.
              </Body>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.candles}
                caption="Plate № 004"
                caption2="Candles at dusk"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 07 · A Legacy Beyond Time ---------------- */}
      <Chapter n="07" title="A legacy" subtitle="beyond time.">
        <Prose>
          <Body>Physical memorials may weather with the passing years.</Body>
          <div className="border-l border-[color:var(--rust)] pl-6 md:pl-8 space-y-3 py-2 my-2">
            {[
              "Photographs can fade.",
              "Letters may become fragile.",
              "Stories may slowly disappear if they are never recorded.",
            ].map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.8vw, 24px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            A digital memorial ensures that these memories remain safe,
            accessible, and meaningful for future generations.
          </Body>
          <Body>
            Children, grandchildren, and great-grandchildren can discover:
          </Body>
          <Ledger items={LEGACY} columns={2} />
          <p
            className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3]"
            style={{ fontSize: "clamp(20px, 2.2vw, 30px)" }}
          >
            A memorial becomes a bridge between generations.
          </p>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 08 · Connected to Your Family Tree ---------------- */}
      <Chapter n="08" title="Connected to" subtitle="your family tree.">
        <Prose>
          <Body>Every memorial is part of a larger story.</Body>
          <Body>
            Celebrate a Life seamlessly connects with <MFT />, allowing loved
            ones to remain part of the family narrative. Each memorial can be
            linked to:
          </Body>
          <Ledger items={CONNECT} columns={3} />
          <Body>
            Future generations can understand not only who someone was, but how
            they fit into the larger story of the family.
          </Body>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/category/genealogy"
              className="btn-ghost text-[13px]"
              data-testid="mem-cta-genealogy"
            >
              Explore Genealogy
            </Link>
            <Link
              to="/cemeteries"
              className="btn-ghost text-[13px]"
              data-testid="mem-cta-cemeteries"
            >
              Browse Cemeteries
            </Link>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 09 · Remember Every Anniversary ---------------- */}
      <Chapter n="09" title="Remember" subtitle="every anniversary.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-7">
              <Body>Love does not end with remembrance.</Body>
              <Body>Celebrate important dates such as:</Body>
              <Ledger items={ANNIVERSARIES} columns={2} />
              <Body>
                These moments provide opportunities to reflect, reconnect, and
                celebrate a life that continues to inspire.
              </Body>
            </div>
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.memorial}
                caption="Plate № 005"
                caption2="At the water's edge"
              />
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 10 · A Living Memorial ---------------- */}
      <Chapter n="10" title="A living" subtitle="memorial.">
        <Prose>
          <Body>Memorials are not static pages.</Body>
          <Body>
            They continue to grow as families contribute new stories,
            photographs, videos, historical research, and memories. Over time,
            they become one of the most meaningful gifts a family can leave for
            future generations.
          </Body>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 py-2">
            {[
              "A place where children can learn about grandparents they never met.",
              "A place where distant relatives can reconnect.",
              "A place where family history becomes personal.",
              "A place where love continues to live.",
            ].map((line, i) => (
              <div key={i} className="border-t border-[color:var(--ink)] pt-4">
                <div className="numeric-index text-[color:var(--ink-soft)] mb-1">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <p
                  className="font-serif-display leading-[1.15]"
                  style={{ fontSize: "clamp(20px, 2vw, 28px)" }}
                >
                  {line}
                </p>
              </div>
            ))}
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 11 · More Than a Memory ---------------- */}
      <Chapter n="11" title="More than" subtitle="a memory.">
        <Prose>
          <Body>
            The greatest legacy we leave behind is not wealth or possessions.
          </Body>
          <div className="border-l border-[color:var(--rust)] pl-6 md:pl-8 space-y-3 py-2 my-2">
            {LEGACY_LINES.map((line, i) => (
              <p
                key={i}
                className="font-italic-serif italic leading-[1.4]"
                style={{ fontSize: "clamp(18px, 1.9vw, 26px)" }}
              >
                {line}
              </p>
            ))}
          </div>
          <Body>
            These are the memories that deserve to be preserved forever.
            Celebrate a Life ensures that every person is remembered not simply
            for when they lived, but for how they lived and the lives they
            touched.
          </Body>
        </Prose>
      </Chapter>

      {/* ---------------- Chapter 12 · Create an Eternal Memory ---------------- */}
      <Chapter n="12" title="Create an" subtitle="eternal memory.">
        <Prose>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="hidden lg:block lg:col-span-5">
              <Plate
                src={PLATE.celebrate}
                caption="Plate № 006"
                caption2="A life, retold"
              />
            </div>
            <div className="lg:col-span-7">
              <Body>Every life deserves more than a simple record.</Body>
              <div className="border-l border-[color:var(--ink)] pl-6 md:pl-8 space-y-2 py-2 my-2">
                {ETERNAL_LINES.map((line, i) => (
                  <p
                    key={i}
                    className="font-italic-serif italic leading-[1.4]"
                    style={{ fontSize: "clamp(19px, 2vw, 28px)" }}
                  >
                    {line}
                  </p>
                ))}
              </div>
              <Body>
                By creating a memorial on Celebrate a Life, you are giving
                future generations the opportunity to know, appreciate, and
                learn from the remarkable people who shaped your family.
              </Body>
              <p
                className="font-italic-serif italic text-[color:var(--rust)] leading-[1.3]"
                style={{ fontSize: "clamp(22px, 2.6vw, 34px)" }}
              >
                Because remembrance is one of the greatest acts of love. And
                love, when shared through memories, never truly fades.
              </p>
            </div>
          </div>
        </Prose>
      </Chapter>

      {/* ---------------- Five pillars strip ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-24 border-t border-[color:var(--ink-line)]">
        <div className="overline text-[color:var(--ink-soft)] mb-8">
          § Remember · Honour · Celebrate · Forever
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.06, ease }}
                className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-7 min-h-[240px] flex flex-col justify-between bg-[color:var(--bone)] hover:bg-[color:var(--bone-2)]"
                style={{ transition: "background-color 400ms ease" }}
                data-testid={`mem-pillar-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-full border border-[color:var(--ink)] flex items-center justify-center text-[color:var(--rust)]">
                  <Icon size={20} />
                </div>
                <div>
                  <div
                    className="font-serif-display leading-[1.1] mt-6"
                    style={{ fontSize: "clamp(18px, 1.7vw, 24px)" }}
                  >
                    {p.label}
                  </div>
                  <p className="mt-2 text-[13px] leading-[1.5] text-[color:var(--ink-soft)]">
                    {p.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Bottom action strip (illustration icons) ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-10 pb-12 md:pb-16">
        <div className="grid grid-cols-3 md:grid-cols-9 gap-0 border-l border-t border-[color:var(--ink-line)]">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.label}
                className="border-r border-b border-[color:var(--ink-line)] p-3 md:p-5 flex flex-col items-center justify-center gap-2 min-h-[110px] text-center"
              >
                <Icon size={18} className="text-[color:var(--rust)]" />
                <div
                  className="numeric-index text-[color:var(--ink)] leading-tight"
                  style={{ fontSize: "10px" }}
                >
                  {a.label}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---------------- Closing manifesto ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-24 border-t border-[color:var(--ink)]">
        <div className="overline text-[color:var(--ink-soft)] mb-6">
          § A life remembered · A love that lives on
        </div>
        <p
          className="font-serif-display leading-[1.02] max-w-5xl mb-6"
          style={{ fontSize: "clamp(30px, 4vw, 62px)" }}
        >
          Every life has a story.{" "}
          <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
            Every story deserves to be remembered. Every memory lives on.
          </span>
        </p>
        <Body>
          As part of <MFT /> and the <SP /> ecosystem, Celebrate a Life —
          Memorials is a lasting sanctuary where families can preserve the
          stories, values, photographs, voices, and legacies of those they
          love. Together, we ensure that future generations inherit not only
          names and dates, but the lives, dreams, and enduring spirit of the
          people who came before them.
        </Body>
        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href={AUTH_SIGNUP_URL}
            className="btn-rust"
            data-testid="mem-cta-primary"
          >
            Create a memorial <span aria-hidden>→</span>
          </a>
          <Link
            to="/category/celebrate-a-life"
            className="btn-ghost"
            data-testid="mem-cta-celebrate"
          >
            Read Celebrate a Life
          </Link>
          <Link
            to="/cemeteries"
            className="btn-ghost"
            data-testid="mem-cta-cem"
          >
            Cemeteries & Mausoleums
          </Link>
        </div>
      </section>

      {/* ---------------- Signature ---------------- */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="border-t border-[color:var(--ink-line)] pt-10">
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <div
                className="font-serif-display leading-[0.95]"
                style={{ fontSize: "clamp(40px, 6vw, 96px)" }}
              >
                Celebrate a Life
                <span className="text-[color:var(--rust)]"> · </span>
                <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                  Memorials
                </span>
              </div>
              <div
                className="font-italic-serif italic mt-3 text-[color:var(--ink-soft)]"
                style={{ fontSize: "clamp(16px, 1.8vw, 22px)" }}
              >
                Celebrate their journey · Cherish their memory · Keep their
                love alive.
              </div>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <p
                className="font-italic-serif italic text-[color:var(--ink)] leading-[1.35]"
                style={{ fontSize: "clamp(16px, 1.6vw, 20px)" }}
              >
                Your life was a blessing. Your memory, a treasure. You are
                loved beyond words, and missed beyond measure.
              </p>
              <div className="mt-6 numeric-index text-[color:var(--ink-soft)]">
                In our hearts always · In our lives forever.
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
